import { useEffect, useState, type ReactNode } from 'react';
import { Check, ChevronRight, type LucideIcon } from 'lucide-react';
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
} from '@/components/ui/BottomSheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

/**
 * Mobile sheet building blocks for the Tasks section. All rows and
 * chips clear the 44px tap-target minimum.
 */

export function SheetSection({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function SheetChip({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 text-xs font-medium transition-colors',
        active
          ? 'border-info bg-info text-info-foreground'
          : 'border-border bg-card text-foreground active:bg-secondary',
        className,
      )}
    >
      {active && <Check className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}

export function ActionSheetItem({
  icon: Icon,
  label,
  description,
  onSelect,
  destructive,
  disabled,
  drillDown,
  selected,
}: {
  icon?: LucideIcon;
  label: ReactNode;
  description?: ReactNode;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** Shows a chevron — the row opens a nested list. */
  drillDown?: boolean;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors active:bg-secondary disabled:pointer-events-none disabled:opacity-50',
        destructive ? 'text-destructive' : 'text-foreground',
        selected && 'bg-info-soft/25',
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'h-[18px] w-[18px] shrink-0',
            destructive ? 'text-destructive' : 'text-muted-foreground',
          )}
          strokeWidth={1.8}
        />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{label}</span>
        {description && (
          <span className="block truncate text-xs text-muted-foreground">
            {description}
          </span>
        )}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0 text-info" />}
      {drillDown && (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" />
      )}
    </button>
  );
}

export function ActionSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetHeader title={title} description={description} />
      <BottomSheetBody className="px-2 py-2">{children}</BottomSheetBody>
      {footer && <BottomSheetFooter>{footer}</BottomSheetFooter>}
    </BottomSheet>
  );
}

export interface OptionSheetOption<T extends string> {
  value: T;
  label: ReactNode;
}

/** Single-select picker (priority, status, …) as a bottom sheet. */
export function OptionSheet<T extends string>({
  open,
  onOpenChange,
  title,
  value,
  options,
  onChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  value: T;
  options: OptionSheetOption<T>[];
  onChange: (next: T) => void;
}) {
  return (
    <ActionSheet open={open} onOpenChange={onOpenChange} title={title}>
      {options.map((opt) => (
        <ActionSheetItem
          key={opt.value}
          label={opt.label}
          selected={opt.value === value}
          onSelect={() => {
            if (opt.value !== value) onChange(opt.value);
            onOpenChange(false);
          }}
        />
      ))}
    </ActionSheet>
  );
}

/** One-field form sheet for quick names (rename, new section, …). */
export function TextInputSheet({
  open,
  onOpenChange,
  title,
  description,
  initialValue = '',
  placeholder,
  confirmLabel = 'Save',
  onSubmit,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  onSubmit: (value: string) => void | Promise<void>;
  pending?: boolean;
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();
  const submit = async () => {
    if (!trimmed || pending) return;
    await onSubmit(trimmed);
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetHeader title={title} description={description} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="contents"
      >
        <BottomSheetBody>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            autoFocus
            enterKeyHint="done"
            className="h-11 text-base"
          />
        </BottomSheetBody>
        <BottomSheetFooter>
          <Button
            type="button"
            variant="ghost"
            className="h-11 w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-11 w-full sm:w-auto"
            disabled={!trimmed || pending}
          >
            {confirmLabel}
          </Button>
        </BottomSheetFooter>
      </form>
    </BottomSheet>
  );
}
